import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";
import App from "./App";
import { reportClientError } from "@/services/errorReportingService";

// Polyfill Promise.withResolvers para compatibilidad universal con pdfjs-dist y navegadores
if (typeof Promise.withResolvers === "undefined") {
  // @ts-expect-error polyfill for Promise.withResolvers
  Promise.withResolvers = function <T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// Listeners globales: capturan errores que NO llegan al ErrorBoundary
// (errores en código fuera del árbol React, promesas no manejadas, etc.)
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    if (!event.error && !event.message) return;
    void reportClientError({
      type: "runtime",
      error: event.error || new Error(event.message || "Unknown runtime error"),
    });
    // A module/evaluation error can happen before React mounts, leaving the
    // inline boot screen visible forever. Replace it with a recoverable UI.
    showBootError(event.message || "Se produjo un error al cargar la aplicación.");
  });

  window.addEventListener("unhandledrejection", (event) => {
    void reportClientError({
      type: "unhandled_promise",
      error: event.reason ?? new Error("Unhandled promise rejection"),
    });
    const message = event.reason instanceof Error
      ? event.reason.message
      : "Se produjo un error al cargar la aplicación.";
    showBootError(message);
  });
}

const container = document.getElementById("root");

function showBootError(message: string) {
  if (!container) return;
  container.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;background:linear-gradient(135deg,#f0fdfa 0%,#ccfbf1 100%);color:#0F172A;">
      <div style="max-width:440px;text-align:center;background:#ffffff;border:1px solid #e5e7eb;border-radius:16px;padding:28px 24px;box-shadow:0 10px 30px -10px rgba(6,211,170,0.25);">
        <div style="font-size:13px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#06b394;margin-bottom:10px;">Munilex</div>
        <h1 style="font-size:20px;font-weight:700;margin:0 0 12px;color:#0F172A;">No se pudo iniciar la aplicación</h1>
        <p style="font-size:14px;line-height:1.5;color:#475569;margin:0 0 20px;">${message}</p>
        <button id="ml-retry" style="padding:10px 18px;border-radius:10px;border:0;background:#06D3AA;color:#0F172A;font-weight:700;cursor:pointer;">Reintentar</button>
      </div>
    </div>
  `;
  const btn = document.getElementById("ml-retry");
  btn?.addEventListener("click", () => window.location.reload());
}

try {
  if (!container) throw new Error("Falta el contenedor #root.");
  // Limpiamos el boot loader inline antes de montar React para evitar parpadeos
  // y para que el watchdog pueda detectar correctamente si React no pinta nada.
  container.innerHTML = "";
  createRoot(container).render(
    <HelmetProvider>
      <App />
    </HelmetProvider>
  );
} catch (err: unknown) {
  console.error("[boot] Failed to mount React app", err);
  const message = err instanceof Error ? err.message : "Error inesperado al iniciar.";
  showBootError(message);
}

// Última red de seguridad: si tras 12 s seguimos sin pintar contenido visible,
// mostramos UI de error con reintento manual (sin auto-reload, evita bucles).
setTimeout(() => {
  if (!container) return;
  const stillBootMarkup = container.querySelector(".ml-boot");
  const visibleText = (container.innerText || "").trim();
  // Si seguimos viendo el boot loader o no hay prácticamente texto pintado,
  // asumimos que algo se ha quedado colgado.
  if (stillBootMarkup || visibleText.length < 5) {
    showBootError("La aplicación tarda más de lo normal. Pulsa Reintentar para volver a cargar.");
  }
}, 12000);
